import ky from 'ky'
// Note: Many paths are missing from the generated OpenAPI types
// We use explicit return types instead of path-based inference where needed
import { useAuthStore } from '@/stores/auth'
import { safeRandomUUID } from '@/utils/uuid'
import {
  adaptCustomer,
  adaptCustomersResponse,
  adaptCustomerDetailResponse,
  type CustomersResponse,
  type Customer,
  type CreateCustomerResponse,
} from '@/types/adapters'
import type { EntityAuditTrailResponse, CategoryVariationsResponse } from '@/types/fallbacks'
import type {
  SafeResponse,
  CreateProductItemResponse,
  ApiDataResponse,
  ApiCollectionResponse,
  ShipperExtended,
  ShipmentDetailedExtended,
  ProductItemExtended,
} from '@/types/openapi-fixes'
import type {
  ProductCategory as ProductCategoryFull,
  Collaborator,
  CollaboratorDetailed,
} from '@/types'
import type { Settlement, UnsettledSummary } from '@/types/settlements'
import type {
  ArrivalSummary,
  ArrivalDetailed,
  ArrivalItem,
  ArrivalItemsSummaryResponse,
  ArrivalFinancialSummaryResponse,
} from '@/types/arrivals'
import type {
  Payment,
  PaymentDetailed,
  DeliveryPaymentOptions,
  CustomerFinancialSummary,
  AutoAllocateResponse,
  PaymentActionResponse,
} from '@/types/payments'
import type {
  DashboardSummaryResponse,
  SalesAnalyticsResponse,
  CustomerAnalyticsResponse,
  DeliveryAnalyticsResponse,
  FinancialOverviewResponse,
} from '@/types/analytics'
import type { Delivery, DeliveryDetailed, KanbanData, ProviderFeesData } from '@/types/deliveries'
import type { Sale as SaleExtended } from '@/types/sales'
import type { ZoneMapData } from '@/composables/useZoneMapAPI'
import type { paths as ApiPaths, components as ApiComponents } from '@/types/api'

// Dynamic day-dispatch proposal (transient; from GET deliverer_assignments/propose).
export interface DispatchRunStop {
  id: string
  seq: number
  leg_km: number
  customer?: string | null
  zone_name?: string | null
  address?: string | null
  time_constraint?: string | null
  time_window_start?: string | null
  time_window_end?: string | null
  latitude?: number | null
  longitude?: number | null
}
export interface DispatchRun {
  id: string
  sector: string
  suggested_deliverer: { id: string; name: string } | null
  stop_count: number
  est_minutes: number
  deliveries: DispatchRunStop[]
}
export interface DispatchProposal {
  date: string
  deliverer_count: number
  delivery_count: number
  runs: DispatchRun[]
  unroutable: { id: string; reason: string }[]
  stats: { per_sector: Record<string, number>; assigned_runs: number; unassigned_runs: number }
}

// Schema type aliases from generated OpenAPI types
type Schemas = ApiComponents['schemas']
type ProductItemSchema = Schemas['ProductItem']
type ProductItemDetailedSchema = Schemas['ProductItemDetailed']
type ProductCategorySchema = Schemas['ProductCategory']
type DeliveryZoneSchema = Schemas['DeliveryZone']
type ShipperSchema = Schemas['Shipper']
type ShipmentSummarySchema = Schemas['ShipmentSummary']
type ShipmentDetailedSchema = Schemas['ShipmentDetailed']
type SaleRunnerUpSchema = Schemas['SaleRunnerUp']
type PaymentMethodSchema = Schemas['PaymentMethod']
type PaymentMethodSummarySchema = Schemas['PaymentMethodSummary']
type PaymentSummarySchema = Schemas['PaymentSummary']
type PaymentDetailedSchema = Schemas['PaymentDetailed']
type SaleSchema = SaleExtended
type SaleDetailedSchema = SaleExtended
type VariationAllowanceSummarySchema = Schemas['VariationAllowanceSummary']
type DetailedVariationAllowanceSchema = Schemas['DetailedVariationAllowance']

// Product API data shape (matches ProductsController#product_response)
interface ProductApiData {
  id: string
  name: string
  description?: string
  base_price: number
  base_cost?: number
  brand?: string
  image_url?: string
  category: string
  category_id: string
  category_path: string
  variations_count: number
  items_count: number
  total_stock: number
  active: boolean
  created_at: string
  updated_at: string
  // Detail fields (only in show/create/update)
  profit_margin?: number
  profit_amount?: number
  has_variations?: boolean
  available_variation_types?: Array<{
    id: string
    name: string
    options_count: number
    sample_options: string[]
    total_category_options: number
    has_restrictions: boolean
  }>
  recent_variations?: Array<{
    id: string
    name: string
    attributes: Record<string, string>
    items_count: number
    total_stock: number
  }>
  variation_allowances?: {
    has_restrictions: boolean
    total_count: number
    allowed_variations_count: number
    allowances: Array<Record<string, unknown>>
  }
}

// Generated API types for Orders
type OrdersListResponse =
  ApiPaths['/api/v1/orders']['get']['responses']['200']['content']['application/json']
type OrderDetailResponse = {
  data: ApiComponents['schemas']['OrderDetailed']
}
type OrderActionResponse = {
  data: ApiComponents['schemas']['OrderDetailed']
  meta?: {
    message?: string
  }
}

// Order request types
interface CreateOrderRequest {
  order: {
    customer_id: string
    notes?: string
  }
}

interface UpdateOrderRequest {
  order: {
    notes?: string
    status?: string
  }
}

// Generated API types for Customers
type CustomersListResponse =
  ApiPaths['/api/v1/customers']['get']['responses']['200']['content']['application/json']
type CustomerDetailResponse = {
  data: ApiComponents['schemas']['CustomerDetailed']
}
type CustomerActionResponse = {
  data: ApiComponents['schemas']['Customer']
  meta?: {
    message?: string
  }
}
type CustomerSearchResponse = {
  data: ApiComponents['schemas']['Customer'][]
}

// Currency can be either a string (legacy) or object (new format)
interface CurrencyObject {
  code: string
  symbol: string
  decimal_places: number
}

// Manual types for auth responses (since OpenAPI generation fails)
interface UserWithDetailedOrganization {
  id: string
  username: string
  full_name: string
  role: 'admin' | 'sales' | 'deliverer' | 'accountant'
  lang?: string
  preferred_lang?: string
  organization_lang?: string
  org_branch_id?: string
  organization: {
    id: string
    name: string
    city_display_name: string
    currency: string | CurrencyObject
    business_config?: Record<string, unknown>
    stats?: Record<string, unknown>
    created_at?: string
    updated_at?: string
  }
}

interface LoginResponse {
  user: UserWithDetailedOrganization
  access_token: string
  token_type: string
  expires_in: number
}

interface MagicLinkResponse {
  user: UserWithDetailedOrganization
  message: string
  magic_link_token: string
  remaining_access_count: number
  expires_at: string
}

export interface BranchAccess {
  id: string
  user: {
    id: string
    username: string
    full_name: string
    home_branch_id: string | null
  }
  org_branch: {
    id: string
    name: string
    city: string
  }
  granted_by: {
    id: string
    username: string
    full_name: string
  }
  expires_at: string | null
  permanent: boolean
  active: boolean
  days_until_expiry: number | null
  notes: string | null
  created_at: string
  updated_at: string
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1'

export type ApiResponse<T> = T
export type ApiError = {
  message: string
  status: number
}

class ApiClient {
  public readonly client: typeof ky
  private refreshPromise: Promise<boolean> | null = null
  private readonly baseUrl: string

  constructor() {
    this.baseUrl = API_BASE_URL

    this.client = ky.create({
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // For refresh token cookies
      retry: {
        // Retry on rate-limit + transient server errors. ky honors Retry-After
        // automatically for 413/429/503 when present.
        limit: 3,
        statusCodes: [408, 429, 500, 502, 503, 504],
        backoffLimit: 30000, // cap exponential backoff at 30s
      },
      hooks: {
        beforeRequest: [
          async (request, options) => {
            const authStore = useAuthStore()
            const url = request.url

            // Skip token refresh for unauthenticated endpoints
            const isAuthEndpoint =
              url.includes('/login') ||
              url.includes('/refresh') ||
              url.includes('/magic_link') ||
              url.includes('/set_password') ||
              url.includes('/silent_refresh') ||
              url.endsWith('/demo')

            // Proactively refresh token if it's expiring soon (only for authenticated requests)
            if (!isAuthEndpoint && authStore.hasActiveSession) {
              await authStore.refreshTokenIfNeeded()
            }

            const token = authStore.token
            if (token) {
              request.headers.set('Authorization', `Bearer ${token}`)
            }

            // Add Accept-Language header for internationalized responses
            try {
              const { getCurrentLocale } = await import('@/i18n')
              const currentLang = getCurrentLocale()
              request.headers.set('Accept-Language', currentLang)
            } catch (error) {
              // Fallback to default if i18n not available
              request.headers.set('Accept-Language', 'en')
            }

            // Add correlation ID for request tracing
            const requestId = safeRandomUUID()
            request.headers.set('X-Request-ID', requestId)

            // Store request ID in scope for Sentry integration
            try {
              const Sentry = await import('@sentry/vue')
              Sentry.getCurrentScope().setTag('request_id', requestId)
            } catch {
              // Sentry not available, continue without it
            }

            // Offline-queue for mutations. If the browser is offline AND the
            // request is a mutation (POST/PUT/PATCH/DELETE) AND not an auth
            // endpoint, stash to IndexedDB and throw a typed error so the
            // caller can show a "queued" toast. The queue auto-replays on
            // reconnect with the original Idempotency-Key.
            const method = (request.method || 'GET').toUpperCase()
            const isMutation =
              method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE'
            const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false
            if (isMutation && isOffline && !isAuthEndpoint) {
              const { enqueueMutation, OfflineQueuedError } = await import(
                '@/composables/useOfflineQueue'
              )
              const bodyText = request.body ? await request.clone().text() : null
              const headersOut: Record<string, string> = {}
              request.headers.forEach((v, k) => {
                headersOut[k] = v
              })
              const queued = await enqueueMutation(
                request.url,
                method as 'POST' | 'PUT' | 'PATCH' | 'DELETE',
                headersOut,
                bodyText,
              )
              throw new OfflineQueuedError(queued)
            }
          },
        ],
        afterResponse: [
          async (request, options, response) => {
            // Handle 401 errors by attempting token refresh
            if (
              response.status === 401 &&
              !request.url.includes('refresh') &&
              !request.url.includes('login')
            ) {
              const authStore = useAuthStore()

              // Use single promise for all concurrent 401s to prevent race conditions
              if (!this.refreshPromise) {
                this.refreshPromise = this.refreshToken().finally(() => {
                  this.refreshPromise = null
                })
              }

              const refreshed = await this.refreshPromise
              if (refreshed) {
                // Retry the original request with new token
                const token = authStore.token
                if (token) {
                  const newRequest = new Request(request, {
                    headers: new Headers(request.headers),
                  })
                  newRequest.headers.set('Authorization', `Bearer ${token}`)
                  return ky(newRequest)
                }
              } else if (authStore.memoryOnly) {
                // Live demo (memory-only): never bounce to the app login — it
                // breaks immersion. Re-bootstrap a fresh demo workspace instead
                // so the prospect stays in the live UI.
                if (window.location.pathname !== '/demo') {
                  window.location.href = '/demo'
                }
              } else {
                // Redirect to login if refresh fails
                authStore.logout()
                if (window.location.pathname !== '/login') {
                  window.location.href = '/login'
                }
              }
            }
            return response
          },
        ],
      },
    })
  }

  // Helper to build full URL
  public url(path: string): string {
    // Remove leading slash if present to avoid double slashes
    const cleanPath = path.startsWith('/') ? path.slice(1) : path
    // Ensure baseUrl ends without trailing slash
    const cleanBase = this.baseUrl.endsWith('/') ? this.baseUrl.slice(0, -1) : this.baseUrl
    return `${cleanBase}/${cleanPath}`
  }

  private async refreshToken(): Promise<boolean> {
    const authStore = useAuthStore()

    if (authStore.isRefreshing) {
      // Already refreshing, use Promise-based waiting
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!authStore.isRefreshing) {
            clearInterval(checkInterval)
            resolve(!!authStore.token)
          }
        }, 100)
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkInterval)
          resolve(!!authStore.token)
        }, 10000)
      })
    }

    try {
      authStore.setRefreshing(true)

      // Refresh token is sent via HTTP-only cookie automatically by browser.
      // 30s hard cap so a hung backend (network partition, slow Neon, paperstore
      // wedge propagating up) doesn't block ALL pending requests indefinitely —
      // they all await `refreshPromise`. Caller treats timeout as failed refresh
      // and redirects to /login (graceful, predictable session loss vs. UI freeze).
      const envelope = await this.client
        .post(this.url('refresh'), { timeout: 30_000 })
        .json<ApiDataResponse<LoginResponse & { refresh_token?: string }>>()
      const response = envelope.data

      // Mark session as active (backend rotated the cookie)
      authStore.setActiveSession(true)

      // UserWithDetailedOrganization is structurally compatible with auth store's User type
      authStore.setAuth(
        response.user as Parameters<typeof authStore.setAuth>[0],
        response.access_token,
        response.expires_in,
      )
      return true
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Token refresh failed:', error)
      }
      return false
    } finally {
      authStore.setRefreshing(false)
    }
  }

  // Authentication endpoints
  async login(credentials: { username: string; password: string; remember_me?: boolean }) {
    const envelope = await this.client
      .post(this.url('login'), {
        json: credentials,
      })
      .json<ApiDataResponse<LoginResponse & { refresh_token?: string }>>()
    const response = envelope.data

    const authStore = useAuthStore()
    // UserWithDetailedOrganization is structurally compatible with auth store's User type
    authStore.setAuth(
      response.user as Parameters<typeof authStore.setAuth>[0],
      response.access_token,
      response.expires_in,
    )

    // Mark session as active (refresh token is set via HTTP-only cookie by backend)
    authStore.setActiveSession(true)

    return response
  }

  // Provisions/claims a throwaway demo workspace and logs in automatically.
  // No credentials required; backend sets the refresh cookie like login.
  async startDemo() {
    // A warm pool claim is instant, but an empty pool falls back to a synchronous
    // provision that can take ~15s. Override ky's 10s default so the cold path
    // doesn't abort mid-provision and surface a spurious failure.
    const envelope = await this.client
      .post(this.url('demo'), { timeout: 60000, retry: 0 })
      .json<
        ApiDataResponse<
          LoginResponse & { refresh_token?: string; demo?: { idle_timeout_minutes: number } }
        >
      >()
    const response = envelope.data

    const authStore = useAuthStore()
    authStore.setAuth(
      response.user as Parameters<typeof authStore.setAuth>[0],
      response.access_token,
      response.expires_in,
    )
    authStore.setActiveSession(true)

    return response
  }

  async logout() {
    try {
      await this.client.post(this.url('logout'))
    } finally {
      const authStore = useAuthStore()
      authStore.logout()
    }
  }

  async createUser(userData: {
    user: {
      username: string
      full_name: string
      role: 'admin' | 'sales' | 'deliverer' | 'accountant'
      org_branch_id?: string | null
      admin_scope?: 'organization' | 'branch'
    }
  }) {
    return this.client
      .post(this.url('create_user'), {
        json: userData,
      })
      .json<{
        success: boolean
        message: string
        user: { id: string; slug: string; username: string; full_name: string; role: string }
        magic_link: { url: string; expires_at: string; max_access_count: number }
      }>()
  }

  async setPassword(data: {
    password: string
    password_confirmation: string
    magic_link_token: string
  }) {
    const response = await this.client
      .post(this.url('set_password'), {
        json: data,
      })
      .json<{ user: any; access_token: string; expires_in: number }>()

    const authStore = useAuthStore()
    authStore.setAuth(response.user, response.access_token, response.expires_in)
    return response
  }

  async getMagicLink(token: string) {
    return this.client.get(this.url(`magic_link/${token}`)).json<MagicLinkResponse>()
  }

  async updateUserSettings(settings: { lang: string }) {
    return this.client
      .patch(this.url('user/settings'), {
        json: { settings },
      })
      .json<{ success: boolean; settings: { lang: string } }>()
  }

  async getAvailableLanguages() {
    return this.client
      .get(this.url('user/available_languages'))
      .json<{ languages: Array<{ code: string; name: string }> }>()
  }

  async getUsers() {
    return this.client.get(this.url('users')).json<{
      users: Array<{
        id: string
        slug: string
        username: string
        full_name: string
        role: 'admin' | 'sales' | 'deliverer' | 'accountant'
        phone_numbers: string[]
        password_set: boolean
        created_at: string
        updated_at: string
        notes?: string
      }>
    }>()
  }

  async regenerateMagicLink(userId: string) {
    return this.client.post(this.url(`users/${userId}/regenerate_magic_link`)).json<{
      success: boolean
      message: string
      user: { id: string; username: string; full_name: string; role: string }
      magic_link: { url: string; expires_at: string; max_access_count: number }
    }>()
  }

  // Customers API - Using standardized response format
  async getCustomers(params?: {
    page?: number
    per_page?: number
    search?: string
    delivery_zone_id?: string
    with_credit_balance?: boolean
    with_debt?: boolean
  }): Promise<CustomersListResponse> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
    if (params?.search) searchParams.set('search', params.search)
    if (params?.delivery_zone_id) searchParams.set('delivery_zone_id', params.delivery_zone_id)
    if (params?.with_credit_balance !== undefined)
      searchParams.set('with_credit_balance', params.with_credit_balance.toString())
    if (params?.with_debt !== undefined) searchParams.set('with_debt', params.with_debt.toString())

    const endpoint = searchParams.toString() ? `customers?${searchParams}` : 'customers'
    return this.client.get(this.url(endpoint)).json<CustomersListResponse>()
  }

  async getCustomer(id: string): Promise<CustomerDetailResponse> {
    return this.client.get(this.url(`customers/${id}`)).json<CustomerDetailResponse>()
  }

  async searchCustomers(query: string, limit: number = 10): Promise<CustomerSearchResponse> {
    const searchParams = new URLSearchParams()
    searchParams.set('q', query)
    searchParams.set('limit', limit.toString())

    return this.client
      .get(this.url(`customers/search?${searchParams}`))
      .json<CustomerSearchResponse>()
  }

  async createCustomer(data: any, force: boolean = false): Promise<CustomerActionResponse> {
    const url = force ? 'customers?force=true' : 'customers'

    try {
      return await this.client.post(this.url(url), { json: data }).json<CustomerActionResponse>()
    } catch (error: any) {
      // For 409 errors, try to extract the response body and attach it to the error
      if (error.response && error.response.status === 409) {
        let errorBody = null

        try {
          // Try to parse the response body
          // Note: ky errors might already have the response parsed
          if (typeof error.response.json === 'function') {
            errorBody = await error.response.json()
          } else if (error.response.body) {
            // If body is already available
            errorBody = error.response.body
          }
        } catch (jsonError) {
          // Could not parse 409 response body
        }

        // Always create an enhanced error with whatever we got
        const enhancedError = new Error(`HTTP 409: Conflict`) as Error & {
          status: number
          data: { error: string; code: string } | null
          response: unknown
        }
        enhancedError.status = 409
        enhancedError.data = errorBody || { error: 'Conflict', code: 'UNKNOWN' }
        enhancedError.response = error.response

        throw enhancedError
      }

      // Re-throw the original error for non-409 or if JSON parsing failed
      throw error
    }
  }

  // Products API - OpenAPI compliant with pagination support
  async getProductItems(params?: {
    page?: number
    per_page?: number
    category_id?: string
    search?: string
    owner_type?: string
    owner_id?: string
    in_stock?: boolean
    low_stock?: boolean
    product_id?: string
  }) {
    const searchParams = new URLSearchParams()

    // Pagination parameters
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())

    // Filter parameters
    if (params?.category_id) searchParams.set('category_id', params.category_id)
    if (params?.search) searchParams.set('search', params.search)
    if (params?.owner_type) searchParams.set('owner_type', params.owner_type)
    if (params?.owner_id) searchParams.set('owner_id', params.owner_id)
    if (params?.in_stock !== undefined) searchParams.set('in_stock', params.in_stock.toString())
    if (params?.low_stock !== undefined) searchParams.set('low_stock', params.low_stock.toString())
    if (params?.product_id) searchParams.set('product_id', params.product_id)

    const endpoint = searchParams.toString() ? `product_items?${searchParams}` : 'product_items'

    return this.client.get(this.url(endpoint)).json<ApiCollectionResponse<ProductItemExtended>>()
  }

  async getProductItem(id: string) {
    return this.client
      .get(this.url(`product_items/${id}`))
      .json<ApiDataResponse<ProductItemDetailedSchema>>()
  }

  async createProductItem(data: any) {
    return this.client
      .post(this.url('product_items'), { json: data })
      .json<CreateProductItemResponse>()
  }

  async adjustProductItemStock(id: string, data: { adjustment: number; reason?: string }) {
    return this.client
      .patch(this.url(`product_items/${id}/adjust_stock`), { json: data })
      .json<ApiDataResponse<ProductItemDetailedSchema>>()
  }

  async getStockReport(params?: { format?: string }) {
    const searchParams = new URLSearchParams()
    if (params?.format) searchParams.set('format', params.format)

    const endpoint = searchParams.toString()
      ? `product_items/stock_report?${searchParams}`
      : 'product_items/stock_report'
    return this.client.get(this.url(endpoint)).json<ApiDataResponse<Record<string, unknown>>>()
  }

  // Bulk create product items with the new API
  async bulkCreateProductItems(data: {
    category_id: string
    products_data: Array<{
      product: {
        id?: string
        name?: string
        description?: string
        base_price?: number
        base_cost?: number
        brand?: string
        active?: boolean
      }
      items: Array<{
        name: string
        description?: string
        price: number
        cost?: number
        quantity: number
        sku?: string
        variation_options?: Record<string, string>
        owner?: {
          type: string
          id: string
        }
      }>
    }>
  }) {
    return this.client.post(this.url('product_items/bulk_create'), { json: data }).json<{
      success: boolean
      message: string
      results: {
        products_created: number
        products_updated: number
        items_created: number
        variations_created: number
        errors?: string[]
      }
      summary?: any
      partial_success?: boolean
    }>()
  }

  // Products API (separate from ProductItems)
  async getProducts(params?: {
    page?: number
    per_page?: number
    category_id?: string
    search?: string
  }) {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
    if (params?.category_id) searchParams.set('category_id', params.category_id)
    if (params?.search) searchParams.set('search', params.search)

    const endpoint = searchParams.toString() ? `products?${searchParams}` : 'products'
    return this.client.get(this.url(endpoint)).json<ApiCollectionResponse<ProductApiData>>()
  }

  async getProduct(id: string) {
    return this.client.get(this.url(`products/${id}`)).json<ApiDataResponse<ProductApiData>>()
  }

  async createProduct(data: Record<string, unknown>) {
    return this.client
      .post(this.url('products'), { json: data })
      .json<ApiDataResponse<ProductApiData>>()
  }

  async updateProduct(id: string, data: Record<string, unknown>) {
    return this.client
      .patch(this.url(`products/${id}`), { json: data })
      .json<ApiDataResponse<ProductApiData>>()
  }

  async deleteProduct(id: string) {
    return this.client.delete(this.url(`products/${id}`)).json<ApiDataResponse<null>>()
  }

  async getAvailableCombinations(id: string) {
    return this.client
      .get(this.url(`products/${id}/available_combinations`))
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async generateVariations(id: string, data: any) {
    return this.client
      .post(this.url(`products/${id}/generate_variations`), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async getProductCategories() {
    return this.client
      .get(this.url('product_categories'))
      .json<ApiCollectionResponse<ProductCategoryFull>>()
  }

  async createProductCategory(data: any) {
    return this.client
      .post(this.url('product_categories'), { json: data })
      .json<ApiDataResponse<ProductCategoryFull>>()
  }

  async getProductCategory(id: string) {
    return this.client
      .get(this.url(`product_categories/${id}`))
      .json<ApiDataResponse<ProductCategoryFull>>()
  }

  async updateProductCategory(id: string, data: { name?: string; description?: string }) {
    return this.client
      .patch(this.url(`product_categories/${id}`), { json: { product_category: data } })
      .json<ApiDataResponse<ProductCategoryFull>>()
  }

  async deleteProductCategory(id: string) {
    return this.client.delete(this.url(`product_categories/${id}`)).json<{ message: string }>()
  }

  // ============================================
  // Category Variations API - CRUD operations
  // ============================================

  // Variation types for API responses
  async getVariations(categoryId: string) {
    return this.client.get(this.url(`product_categories/${categoryId}/variations`)).json<{
      success: boolean
      category: { id: string; name: string; full_path: string }
      variations: Array<{
        id: string
        name: string
        description?: string
        position: number
        active: boolean
        product_category_id: string
        options_count: number
        active_options_count: number
        in_use?: boolean
        usage_count?: number
        options: Array<{
          id: string
          value: string
          position: number
          active: boolean
          variation_id: string
          created_at: string
          updated_at: string
        }>
        created_at: string
        updated_at: string
      }>
      meta: {
        total_variations: number
        active_variations: number
        total_options: number
      }
    }>()
  }

  async getVariation(categoryId: string, variationId: string) {
    return this.client
      .get(this.url(`product_categories/${categoryId}/variations/${variationId}`))
      .json<{
        success: boolean
        variation: {
          id: string
          name: string
          description?: string
          position: number
          active: boolean
          product_category_id: string
          options_count: number
          active_options_count: number
          options: Array<{
            id: string
            value: string
            position: number
            active: boolean
            variation_id: string
            created_at: string
            updated_at: string
          }>
          in_use?: boolean
          usage_count?: number
          created_at: string
          updated_at: string
        }
      }>()
  }

  async createVariation(
    categoryId: string,
    data: {
      variation: {
        name: string
        description?: string
        position?: number
        active?: boolean
      }
      options?: string[]
    },
  ) {
    return this.client
      .post(this.url(`product_categories/${categoryId}/variations`), { json: data })
      .json<{
        success: boolean
        message: string
        variation: {
          id: string
          name: string
          description?: string
          position: number
          active: boolean
          product_category_id: string
          options_count: number
          active_options_count: number
          options: Array<{
            id: string
            value: string
            position: number
            active: boolean
            variation_id: string
            created_at: string
            updated_at: string
          }>
          created_at: string
          updated_at: string
        }
      }>()
  }

  async updateVariation(
    categoryId: string,
    variationId: string,
    data: {
      variation: {
        name?: string
        description?: string
        position?: number
        active?: boolean
      }
    },
  ) {
    return this.client
      .patch(this.url(`product_categories/${categoryId}/variations/${variationId}`), { json: data })
      .json<{
        success: boolean
        message: string
        variation: {
          id: string
          name: string
          description?: string
          position: number
          active: boolean
          product_category_id: string
          options_count: number
          active_options_count: number
          options: Array<{
            id: string
            value: string
            position: number
            active: boolean
            variation_id: string
            created_at: string
            updated_at: string
          }>
          created_at: string
          updated_at: string
        }
      }>()
  }

  async deleteVariation(categoryId: string, variationId: string) {
    return this.client
      .delete(this.url(`product_categories/${categoryId}/variations/${variationId}`))
      .json<{ success: boolean; message: string }>()
  }

  async addVariationOptions(categoryId: string, variationId: string, options: string[]) {
    return this.client
      .post(this.url(`product_categories/${categoryId}/variations/${variationId}/add_options`), {
        json: { options },
      })
      .json<{
        success: boolean
        message: string
        options: Array<{
          id: string
          value: string
          position: number
          active: boolean
          variation_id: string
          created_at: string
          updated_at: string
        }>
        variation: {
          id: string
          name: string
          options: Array<{
            id: string
            value: string
            position: number
            active: boolean
            variation_id: string
          }>
        }
      }>()
  }

  async removeVariationOption(categoryId: string, variationId: string, optionId: string) {
    return this.client
      .delete(
        this.url(`product_categories/${categoryId}/variations/${variationId}/options/${optionId}`),
      )
      .json<{
        success: boolean
        message: string
        variation: {
          id: string
          name: string
          options: Array<{
            id: string
            value: string
            position: number
            active: boolean
            variation_id: string
          }>
        }
      }>()
  }

  async updateVariationOption(
    categoryId: string,
    variationId: string,
    optionId: string,
    data: {
      option: {
        value?: string
        position?: number
        active?: boolean
      }
    },
  ) {
    return this.client
      .patch(
        this.url(`product_categories/${categoryId}/variations/${variationId}/options/${optionId}`),
        { json: data },
      )
      .json<{
        success: boolean
        message: string
        option: {
          id: string
          value: string
          position: number
          active: boolean
          variation_id: string
          created_at: string
          updated_at: string
        }
        variation: {
          id: string
          name: string
          options: Array<{
            id: string
            value: string
            position: number
            active: boolean
            variation_id: string
          }>
        }
      }>()
  }

  async reorderVariationOptions(categoryId: string, variationId: string, optionIds: string[]) {
    return this.client
      .post(
        this.url(`product_categories/${categoryId}/variations/${variationId}/reorder_options`),
        {
          json: { option_ids: optionIds },
        },
      )
      .json<{
        success: boolean
        message: string
        variation: {
          id: string
          name: string
          options: Array<{
            id: string
            value: string
            position: number
            active: boolean
            variation_id: string
          }>
        }
      }>()
  }

  async reorderVariations(categoryId: string, variationIds: string[]) {
    return this.client
      .post(this.url(`product_categories/${categoryId}/variations/reorder`), {
        json: { variation_ids: variationIds },
      })
      .json<{
        success: boolean
        message: string
        variations: Array<{
          id: string
          name: string
          position: number
          options: Array<{
            id: string
            value: string
            position: number
            active: boolean
            variation_id: string
          }>
        }>
      }>()
  }

  // Product Variations API
  async getCategoryVariations(categoryId: string): Promise<CategoryVariationsResponse> {
    try {
      const response = await this.client
        .get(this.url(`product_categories/${categoryId}/variations`))
        .json<CategoryVariationsResponse>()

      return response
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to get category variations:', error)
      }

      // Fallback to the previous method if the new endpoint doesn't exist yet
      return this.getCategoryVariationsViaProduct(categoryId)
    }
  }

  // Get product allowed variations
  async getProductAllowedVariations(productId: string) {
    try {
      const response = await this.client
        .get(this.url(`products/${productId}/allowed_variations`))
        .json<
          ApiDataResponse<{
            allowed_variations: Array<{
              id: string
              name: string
              description?: string
              position: number
              allowed_options: Array<{
                id: string
                value: string
                position: number
              }>
            }>
          }>
        >()

      return response
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to get product allowed variations:', error)
      }
      throw error
    }
  }

  // Get product variation allowances
  async getProductVariationAllowances(
    productId: string,
    params?: {
      variation_id?: string
      type?: 'variation_level' | 'option_level'
      allowed?: boolean
    },
  ) {
    try {
      const searchParams = new URLSearchParams()
      if (params?.variation_id) searchParams.set('variation_id', params.variation_id)
      if (params?.type) searchParams.set('type', params.type)
      if (params?.allowed !== undefined) searchParams.set('allowed', params.allowed.toString())

      const endpoint = searchParams.toString()
        ? `products/${productId}/variation_allowances?${searchParams}`
        : `products/${productId}/variation_allowances`

      const response = await this.client
        .get(this.url(endpoint))
        .json<ApiDataResponse<DetailedVariationAllowanceSchema[]>>()

      return response
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to get product variation allowances:', error)
      }
      throw error
    }
  }

  // Create variation allowances in bulk
  async createProductVariationAllowances(productId: string, data: any) {
    try {
      const response = await this.client
        .post(this.url(`products/${productId}/variation_allowances/bulk_create`), {
          json: data,
        })
        .json<ApiDataResponse<{ created: DetailedVariationAllowanceSchema[]; message?: string }>>()

      return response
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to create product variation allowances:', error)
      }
      throw error
    }
  }

  // Legacy method using sample options (creates fabricated IDs)
  async getCategoryVariationsViaProduct(categoryId: string): Promise<CategoryVariationsResponse> {
    try {
      // First get products in this category
      const productsResponse = await this.getProducts({ category_id: categoryId, per_page: 1 })

      if (productsResponse.data && productsResponse.data.length > 0) {
        const productId = productsResponse.data[0].id

        // Get allowed variations for this product to see available variations
        const allowedResponse = await this.getProductAllowedVariations(productId)

        // Transform available_variations to include full option details
        const availableVariations = (allowedResponse.data as Record<string, unknown>)
          ?.allowed_variations as Array<Record<string, unknown>> | undefined
        const variationsWithOptions = availableVariations?.map((variation) => ({
          id: variation.id as string,
          name: variation.name as string,
          description: variation.description as string | undefined,
          position: (variation.position as number) || 0,
          active: (variation.active as boolean) ?? true,
          product_category_id: categoryId,
          // Convert allowed_options to full option objects with IDs
          variation_options:
            (variation.allowed_options as Array<Record<string, unknown>> | undefined)?.map(
              (option, index) => ({
                id: (option.id || `${variation.id}-option-${index}`) as string,
                value: option.value as string,
                position: (option.position as number) ?? index + 1,
                active: true,
                variation_id: variation.id as string,
                is_sample: !option.id,
              }),
            ) || [],
        }))

        return {
          success: true,
          category_id: categoryId,
          available_variations: variationsWithOptions,
          reference_product_id: productId,
          note: 'Using sample options - full option list requires direct category variations endpoint',
        }
      }

      return {
        success: true,
        category_id: categoryId,
        available_variations: [],
        reference_product_id: null,
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to get category variations:', error)
      }
      return {
        success: false,
        category_id: categoryId,
        available_variations: [],
        reference_product_id: null,
        error: error,
      }
    }
  }

  // Single variation allowance creation
  async createProductVariationAllowance(productId: string, data: any) {
    return this.client
      .post(this.url(`products/${productId}/variation_allowances`), { json: data })
      .json<ApiDataResponse<DetailedVariationAllowanceSchema>>()
  }

  // Deprecated: Use createProductVariationAllowances instead
  async bulkCreateProductVariationAllowances(
    productId: string,
    data: {
      allowances: Array<{
        variation_id: string
        variation_option_id?: string
        allowed: boolean
        active?: boolean
        position?: number
      }>
    },
  ) {
    // Convert to new format
    const newData = {
      allowances: data.allowances.map((allowance) => ({
        variation_id: allowance.variation_id,
        variation_option_id: allowance.variation_option_id || null,
        allowed: allowance.allowed,
        active: allowance.active ?? true,
      })),
    }

    return this.createProductVariationAllowances(productId, newData)
  }

  // Sales API
  async getSales(params?: {
    status?: string
    page?: number
    customer_id?: string
    per_page?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.customer_id) searchParams.set('customer_id', params.customer_id)
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())

    const endpoint = searchParams.toString() ? `sales?${searchParams}` : 'sales'
    return this.client.get(this.url(endpoint)).json<ApiCollectionResponse<SaleSchema>>()
  }

  async getSale(id: string) {
    return this.client.get(this.url(`sales/${id}`)).json<ApiDataResponse<SaleSchema>>()
  }

  async createSale(data: Record<string, unknown>) {
    return this.client.post(this.url('sales'), { json: data }).json<ApiDataResponse<SaleSchema>>()
  }

  async cancelSale(id: string) {
    return this.client.patch(this.url(`sales/${id}/cancel`)).json<ApiDataResponse<SaleSchema>>()
  }

  async confirmSale(id: string) {
    return this.client.patch(this.url(`sales/${id}/confirm`)).json<ApiDataResponse<SaleSchema>>()
  }

  async deliverSale(id: string) {
    return this.client.patch(this.url(`sales/${id}/deliver`)).json<ApiDataResponse<SaleSchema>>()
  }

  async getSaleRunnerUpsBySale(id: string) {
    return this.client
      .get(this.url(`sales/${id}/runner_ups`))
      .json<ApiDataResponse<{ runner_ups: SaleRunnerUpSchema[] }>>()
  }

  async createRecoverySale(saleId: string, data: Record<string, unknown>) {
    return this.client
      .post(this.url(`sales/${saleId}/create_recovery_sale`), { json: data })
      .json<ApiDataResponse<SaleSchema>>()
  }

  // Sale Runner Ups API
  async getSaleRunnerUps(params?: {
    status?: string
    product_item_id?: string
    customer_id?: string
    page?: number
    per_page?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.product_item_id) searchParams.set('product_item_id', params.product_item_id)
    if (params?.customer_id) searchParams.set('customer_id', params.customer_id)
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())

    const endpoint = searchParams.toString() ? `sale_runner_ups?${searchParams}` : 'sale_runner_ups'
    return this.client.get(this.url(endpoint)).json<ApiCollectionResponse<SaleRunnerUpSchema>>()
  }

  async createSaleRunnerUp(data: any) {
    return this.client
      .post(this.url('sale_runner_ups'), { json: data })
      .json<ApiDataResponse<SaleRunnerUpSchema>>()
  }

  async updateSaleRunnerUp(id: string, data: any) {
    return this.client
      .patch(this.url(`sale_runner_ups/${id}`), { json: data })
      .json<ApiDataResponse<SaleRunnerUpSchema>>()
  }

  async moveSaleRunnerUpPriority(id: string, direction: 'up' | 'down') {
    return this.client
      .patch(this.url(`sale_runner_ups/${id}/move_priority`), { json: { direction } })
      .json<ApiDataResponse<SaleRunnerUpSchema>>()
  }

  // Refunds API
  async getRefunds(params?: { page?: number; per_page?: number }) {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())

    const endpoint = searchParams.toString() ? `refunds?${searchParams}` : 'refunds'
    return this.client
      .get(this.url(endpoint))
      .json<ApiCollectionResponse<Record<string, unknown>>>()
  }

  async createRefund(data: {
    sale_id: string
    amount_cents: number
    reason: string
    payment_id?: string
    idempotency_key?: string
  }) {
    return this.client
      .post(this.url('refunds'), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  // Reversals API
  async getReversals(params?: { page?: number; per_page?: number }) {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())

    const endpoint = searchParams.toString() ? `reversals?${searchParams}` : 'reversals'
    return this.client
      .get(this.url(endpoint))
      .json<ApiCollectionResponse<Record<string, unknown>>>()
  }

  async createReversal(data: {
    collaborator_settlement_id: string
    reason: string
    refund_id?: string
    idempotency_key?: string
  }) {
    return this.client
      .post(this.url('reversals'), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  // Collaboration Closeout API
  async closeoutCollaboration(
    collaborationId: string,
    data: {
      reason: string
      stock_disposition: 'return_to_collaborator' | 'keep_at_organization' | 'write_off'
      terminated_at?: string
    },
  ) {
    return this.client
      .post(this.url(`collaborations/${collaborationId}/closeout`), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  // Orders API - Using standardized response format
  async getOrders(params?: {
    status?: string
    customer_id?: string
    page?: number
    per_page?: number
    search?: string
    sort_by?: string
    sort_direction?: 'asc' | 'desc'
    start_date?: string
    end_date?: string
    min_total?: number
    max_total?: number
    // Legacy aliases
    sort?: string
    created_before?: string
    created_after?: string
  }): Promise<OrdersListResponse> {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.customer_id) searchParams.set('customer_id', params.customer_id)
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
    if (params?.search) searchParams.set('search', params.search)
    // Support both new and legacy sort params
    if (params?.sort_by) searchParams.set('sort_by', params.sort_by)
    if (params?.sort_direction) searchParams.set('sort_direction', params.sort_direction)
    if (params?.sort) searchParams.set('sort_by', params.sort) // Legacy alias
    // Date filters
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)
    if (params?.created_before) searchParams.set('end_date', params.created_before) // Legacy alias
    if (params?.created_after) searchParams.set('start_date', params.created_after) // Legacy alias
    // Amount filters
    if (params?.min_total !== undefined) searchParams.set('min_total', params.min_total.toString())
    if (params?.max_total !== undefined) searchParams.set('max_total', params.max_total.toString())

    const endpoint = searchParams.toString() ? `orders?${searchParams}` : 'orders'
    return this.client.get(this.url(endpoint)).json<OrdersListResponse>()
  }

  async getOrder(id: string): Promise<OrderDetailResponse> {
    return this.client.get(this.url(`orders/${id}`)).json<OrderDetailResponse>()
  }

  async createOrder(data: CreateOrderRequest): Promise<OrderActionResponse> {
    return this.client.post(this.url('orders'), { json: data }).json<OrderActionResponse>()
  }

  async updateOrder(id: string, data: UpdateOrderRequest): Promise<OrderActionResponse> {
    return this.client.patch(this.url(`orders/${id}`), { json: data }).json<OrderActionResponse>()
  }

  async completeOrder(id: string): Promise<OrderActionResponse> {
    return this.client.patch(this.url(`orders/${id}/complete`)).json<OrderActionResponse>()
  }

  async cancelOrder(id: string, reason?: string): Promise<OrderActionResponse> {
    const json = reason ? { reason } : undefined
    return this.client.patch(this.url(`orders/${id}/cancel`), { json }).json<OrderActionResponse>()
  }

  async confirmOrder(id: string): Promise<OrderActionResponse> {
    return this.client.patch(this.url(`orders/${id}/confirm`)).json<OrderActionResponse>()
  }

  async deleteOrder(id: string): Promise<{ meta: { message: string } }> {
    return this.client.delete(this.url(`orders/${id}`)).json<{ meta: { message: string } }>()
  }

  // Order Audit Trail - Get history of changes for an order
  async getOrderAuditTrail(orderId: string): Promise<EntityAuditTrailResponse> {
    return this.client
      .get(this.url(`entities/order/${orderId}/audit_trail`))
      .json<EntityAuditTrailResponse>()
  }

  // Deliveries API - OpenAPI compliant
  async getDeliveries(params?: {
    customer_id?: string
    status?: string
    delivery_zone_id?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
    search?: string
    local_only?: boolean
    cross_city_only?: boolean
  }) {
    const searchParams = new URLSearchParams()
    if (params?.customer_id) searchParams.set('customer_id', params.customer_id)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.delivery_zone_id) searchParams.set('delivery_zone_id', params.delivery_zone_id)
    if (params?.date_from) searchParams.set('date_from', params.date_from)
    if (params?.date_to) searchParams.set('date_to', params.date_to)
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
    if (params?.search) searchParams.set('search', params.search)
    if (params?.local_only) searchParams.set('local_only', 'true')
    if (params?.cross_city_only) searchParams.set('cross_city_only', 'true')

    const endpoint = searchParams.toString() ? `deliveries?${searchParams}` : 'deliveries'
    return this.client.get(this.url(endpoint)).json<ApiCollectionResponse<Delivery>>()
  }

  async getDelivery(id: string) {
    return this.client.get(this.url(`deliveries/${id}`)).json<ApiDataResponse<DeliveryDetailed>>()
  }

  async updateDelivery(id: string, data: unknown) {
    return this.client
      .patch(this.url(`deliveries/${id}`), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async assignDelivery(id: string, data: { deliverer_id: string }) {
    return this.client
      .patch(this.url(`deliveries/${id}/assign`), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async bulkAssignDeliveries(data: { deliverer_id: string; delivery_ids: string[] }) {
    return this.client
      .post(this.url('deliveries/bulk_assign'), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async bulkReassignDeliveries(data: { deliverer_id: string; delivery_ids: string[] }) {
    return this.client
      .patch(this.url('deliveries/bulk_reassign'), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  // Dynamic day-dispatch: transient per-deliverer run proposals (no writes).
  async getDispatchProposal(date?: string) {
    const url = date
      ? this.url(`deliverer_assignments/propose?date=${encodeURIComponent(date)}`)
      : this.url('deliverer_assignments/propose')
    return this.client.get(url).json<ApiDataResponse<DispatchProposal>>()
  }

  async unassignDelivery(id: string, data?: { reason?: string }) {
    return this.client
      .patch(this.url(`deliveries/${id}/unassign`), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async startDelivery(id: string, data?: { notes?: string }) {
    return this.client
      .patch(this.url(`deliveries/${id}/start`), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async completeDelivery(
    id: string,
    data?: {
      delivered_time?: string
      notes?: string
      defer_payment?: boolean
      deferral_reason?: string
      deferred_until?: string
    },
  ) {
    return this.client
      .patch(this.url(`deliveries/${id}/complete`), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async markDeliveryFailed(id: string, data?: { reason?: string }) {
    return this.client
      .patch(this.url(`deliveries/${id}/mark_failed`), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async retryDelivery(id: string) {
    return this.client
      .patch(this.url(`deliveries/${id}/retry`))
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async reactivateDelivery(id: string, data?: { reason?: string }) {
    return this.client
      .patch(this.url(`deliveries/${id}/reactivate`), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async cancelDelivery(id: string, data?: { reason?: string }) {
    return this.client
      .patch(this.url(`deliveries/${id}/cancel`), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async rescheduleDelivery(
    id: string,
    data: { scheduled_date: string; scheduled_time_slot?: string },
  ) {
    return this.client
      .patch(this.url(`deliveries/${id}/reschedule`), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async createShipmentForDelivery(id: string, data: { shipper_id: string }) {
    return this.client
      .post(this.url(`deliveries/${id}/create_shipment`), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async getDeliveryTracking(id: string) {
    return this.client
      .get(this.url(`deliveries/${id}/tracking`))
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  // Get payment options for a delivery (credit info, lock version)
  async getDeliveryPaymentOptions(id: string) {
    return this.client
      .get(this.url(`deliveries/${id}/payment_options`))
      .json<ApiDataResponse<DeliveryPaymentOptions>>()
  }

  // Collect payment from delivery (with credit and optimistic locking support)
  async collectDeliveryPayment(
    id: string,
    data: {
      amount: number
      payment_method?: string
      notes?: string
      apply_credit?: boolean
      lock_version?: number
    },
  ) {
    return this.client
      .post(this.url(`deliveries/${id}/collect_payment`), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  // Defer payment collection for delivery
  async deferDeliveryPayment(
    id: string,
    data: {
      reason: string
      until_date?: string
    },
  ) {
    return this.client
      .post(this.url(`deliveries/${id}/defer_payment`), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  // Get outstanding provider fees (admin)
  async getOutstandingProviderFees(params?: {
    shipper_id?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.shipper_id) searchParams.set('shipper_id', params.shipper_id)
    if (params?.date_from) searchParams.set('date_from', params.date_from)
    if (params?.date_to) searchParams.set('date_to', params.date_to)
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())

    const endpoint = searchParams.toString()
      ? `deliveries/outstanding_provider_fees?${searchParams}`
      : 'deliveries/outstanding_provider_fees'
    return this.client.get(this.url(endpoint)).json<ApiDataResponse<ProviderFeesData>>()
  }

  // Mark single provider fee as paid (admin)
  async markProviderFeePaid(
    id: string,
    data?: {
      paid_at?: string
      notes?: string
    },
  ) {
    return this.client
      .post(this.url(`deliveries/${id}/mark_provider_fee_paid`), { json: data || {} })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  // Bulk mark provider fees as paid (admin)
  async bulkMarkProviderFeesPaid(data: {
    delivery_ids: string[]
    paid_at?: string
    notes?: string
  }) {
    return this.client
      .post(this.url('deliveries/bulk_mark_provider_fees_paid'), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async getDeliveriesKanban(params?: {
    deliverer_id?: string
    zone_id?: string
    scheduled_date?: string
  }) {
    const searchParams = new URLSearchParams()
    if (params?.deliverer_id) searchParams.set('deliverer_id', params.deliverer_id)
    if (params?.zone_id) searchParams.set('zone_id', params.zone_id)
    if (params?.scheduled_date) searchParams.set('scheduled_date', params.scheduled_date)

    const endpoint = searchParams.toString()
      ? `deliveries/kanban?${searchParams}`
      : 'deliveries/kanban'
    return this.client.get(this.url(endpoint)).json<ApiDataResponse<KanbanData>>()
  }

  async getMyDeliveries(params?: { status?: string; page?: number; per_page?: number }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())

    const endpoint = searchParams.toString()
      ? `deliveries/my_deliveries?${searchParams}`
      : 'deliveries/my_deliveries'
    return this.client.get(this.url(endpoint)).json<ApiDataResponse<Record<string, unknown>>>() // my_deliveries has non-standard nested shape
  }

  // Delivery Zones API - OpenAPI compliant
  async getDeliveryZones(params?: {
    search?: string
    active?: boolean
    bucket?: string
    page?: number
    per_page?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.search) searchParams.set('search', params.search)
    if (params?.active !== undefined) searchParams.set('active', params.active.toString())
    if (params?.bucket) searchParams.set('bucket', params.bucket)
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())

    const endpoint = searchParams.toString() ? `delivery_zones?${searchParams}` : 'delivery_zones'
    return this.client.get(this.url(endpoint)).json<ApiCollectionResponse<DeliveryZoneSchema>>()
  }

  async getDeliveryZone(id: string) {
    return this.client
      .get(this.url(`delivery_zones/${id}`))
      .json<ApiDataResponse<DeliveryZoneSchema>>()
  }

  async createDeliveryZone(data: {
    delivery_zone: {
      name: string
      zone_bucket?: string
      delivery_cost?: number
      description?: string
    }
  }) {
    return this.client
      .post(this.url('delivery_zones'), { json: data })
      .json<ApiDataResponse<DeliveryZoneSchema>>()
  }

  async updateDeliveryZone(id: string, data: any) {
    return this.client
      .patch(this.url(`delivery_zones/${id}`), { json: data })
      .json<ApiDataResponse<DeliveryZoneSchema>>()
  }

  async deleteDeliveryZone(id: string, reassignTo?: string) {
    const path = reassignTo
      ? `delivery_zones/${id}?reassign_to=${encodeURIComponent(reassignTo)}`
      : `delivery_zones/${id}`
    return this.client.delete(this.url(path)).json<ApiDataResponse<null>>()
  }

  async createDefaultDeliveryZones() {
    return this.client
      .post(this.url('delivery_zones/create_defaults'))
      .json<ApiDataResponse<{ created: DeliveryZoneSchema[]; message?: string }>>()
  }

  // Geographic map payload: zones with coordinates + shipper offices in the
  // org city, for the interactive MapLibre admin view.
  async getZoneMapData() {
    return this.client.get(this.url('delivery_zones/map')).json<ApiDataResponse<ZoneMapData>>()
  }

  // Shippers API - OpenAPI compliant
  async getShippers(params?: {
    search?: string
    status?: string
    service_type?: string
    page?: number
    per_page?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.search) searchParams.set('search', params.search)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.service_type) searchParams.set('service_type', params.service_type)
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())

    const endpoint = searchParams.toString() ? `shippers?${searchParams}` : 'shippers'
    return this.client.get(this.url(endpoint)).json<ApiCollectionResponse<ShipperSchema>>()
  }

  async getShipper(id: string) {
    return this.client.get(this.url(`shippers/${id}`)).json<ApiDataResponse<ShipperExtended>>()
  }

  async createShipper(data: {
    shipper: {
      name: string
      service_type: string
      contact_info?: any
      capabilities?: any
      pricing?: any
    }
  }) {
    return this.client
      .post(this.url('shippers'), { json: data })
      .json<ApiDataResponse<ShipperSchema>>()
  }

  async updateShipper(id: string, data: any) {
    return this.client
      .patch(this.url(`shippers/${id}`), { json: data })
      .json<ApiDataResponse<ShipperSchema>>()
  }

  async activateShipper(id: string) {
    return this.client
      .patch(this.url(`shippers/${id}/activate`))
      .json<ApiDataResponse<ShipperSchema>>()
  }

  async suspendShipper(id: string) {
    return this.client
      .patch(this.url(`shippers/${id}/suspend`))
      .json<ApiDataResponse<ShipperSchema>>()
  }

  async terminateShipper(id: string) {
    return this.client
      .patch(this.url(`shippers/${id}/terminate`))
      .json<ApiDataResponse<ShipperSchema>>()
  }

  // Enhanced Shipments API - OpenAPI compliant
  async getShipments(params?: {
    status?: string
    shipper_id?: string
    customer_id?: string
    page?: number
    per_page?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.shipper_id) searchParams.set('shipper_id', params.shipper_id)
    if (params?.customer_id) searchParams.set('customer_id', params.customer_id)
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())

    const endpoint = searchParams.toString() ? `shipments?${searchParams}` : 'shipments'
    return this.client.get(this.url(endpoint)).json<ApiCollectionResponse<ShipmentSummarySchema>>()
  }

  async getShipment(id: string) {
    return this.client
      .get(this.url(`shipments/${id}`))
      .json<ApiDataResponse<ShipmentDetailedExtended>>()
  }

  async createShipment(data: any) {
    return this.client
      .post(this.url('shipments'), { json: data })
      .json<ApiDataResponse<ShipmentSummarySchema>>()
  }

  async updateShipment(id: string, data: any) {
    return this.client
      .patch(this.url(`shipments/${id}`), { json: data })
      .json<ApiDataResponse<ShipmentSummarySchema>>()
  }

  async scheduleShipment(id: string, data?: { scheduled_date?: string }) {
    return this.client
      .patch(this.url(`shipments/${id}/schedule`), { json: data || {} })
      .json<ApiDataResponse<ShipmentSummarySchema>>()
  }

  async initiateShipment(id: string, data?: { tracking_notes?: string }) {
    return this.client
      .patch(this.url(`shipments/${id}/initiate`), { json: data || {} })
      .json<ApiDataResponse<ShipmentSummarySchema>>()
  }

  async markShipmentInTransit(id: string, data?: { location_update?: string }) {
    return this.client
      .patch(this.url(`shipments/${id}/mark_in_transit`), { json: data || {} })
      .json<ApiDataResponse<ShipmentSummarySchema>>()
  }

  async markShipmentSuccess(id: string, data?: { delivered_at?: string; notes?: string }) {
    return this.client
      .patch(this.url(`shipments/${id}/mark_success`), { json: data || {} })
      .json<ApiDataResponse<ShipmentSummarySchema>>()
  }

  async markShipmentReturned(id: string, data?: { return_reason?: string; return_notes?: string }) {
    return this.client
      .patch(this.url(`shipments/${id}/mark_returned`), { json: data || {} })
      .json<ApiDataResponse<ShipmentSummarySchema>>()
  }

  async markShipmentFailed(id: string, data?: { failure_reason?: string; failure_notes?: string }) {
    return this.client
      .patch(this.url(`shipments/${id}/mark_failed`), { json: data || {} })
      .json<ApiDataResponse<ShipmentSummarySchema>>()
  }

  async cancelShipment(id: string, data?: { cancellation_reason?: string; notes?: string }) {
    return this.client
      .delete(this.url(`shipments/${id}/cancel`), { json: data || {} })
      .json<ApiDataResponse<ShipmentSummarySchema>>()
  }

  async reassignShipment(id: string, data: { new_shipper_id: string; reason?: string }) {
    return this.client
      .patch(this.url(`shipments/${id}/reassign`), { json: data })
      .json<ApiDataResponse<ShipmentSummarySchema>>()
  }

  async getShipmentTrackingHistory(id: string) {
    return this.client
      .get(this.url(`shipments/${id}/tracking_history`))
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  async bulkCreateShipments(data: {
    shipments: Array<{
      delivery_id: string
      shipper_id: string
      tracking_number?: string
      estimated_arrival?: string
      cost?: number
      notes?: string
    }>
  }) {
    return this.client
      .post(this.url('shipments/bulk_create'), { json: data })
      .json<ApiDataResponse<{ created: ShipmentSummarySchema[]; message?: string }>>()
  }

  async getShipmentsStatistics(params?: {
    date_from?: string
    date_to?: string
    group_by?: 'day' | 'week' | 'month' | 'shipper' | 'zone'
    shipper_id?: string
    zone_id?: string
  }) {
    const searchParams = new URLSearchParams()
    if (params?.date_from) searchParams.set('date_from', params.date_from)
    if (params?.date_to) searchParams.set('date_to', params.date_to)
    if (params?.group_by) searchParams.set('group_by', params.group_by)
    if (params?.shipper_id) searchParams.set('shipper_id', params.shipper_id)
    if (params?.zone_id) searchParams.set('zone_id', params.zone_id)

    const endpoint = searchParams.toString()
      ? `shipments/statistics?${searchParams}`
      : 'shipments/statistics'
    return this.client.get(this.url(endpoint)).json<ApiDataResponse<Record<string, unknown>>>()
  }

  // Collaborators API - External consignment partners
  async getCollaborators(params?: {
    status?: string
    business_type?: string
    active_only?: boolean
    search?: string
    sort_by?: string
    sort_direction?: 'asc' | 'desc'
    page?: number
    per_page?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.business_type) searchParams.set('business_type', params.business_type)
    if (params?.active_only) searchParams.set('active_only', 'true')
    if (params?.search) searchParams.set('search', params.search)
    if (params?.sort_by) searchParams.set('sort_by', params.sort_by)
    if (params?.sort_direction) searchParams.set('sort_direction', params.sort_direction)
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())

    const endpoint = searchParams.toString() ? `collaborators?${searchParams}` : 'collaborators'
    return this.client.get(this.url(endpoint)).json<ApiCollectionResponse<Collaborator>>()
  }

  async getCollaborator(id: string) {
    return this.client
      .get(this.url(`collaborators/${id}`))
      .json<ApiDataResponse<CollaboratorDetailed>>()
  }

  async createCollaborator(data: Record<string, unknown>) {
    return this.client
      .post(this.url('collaborators'), { json: data })
      .json<ApiDataResponse<Collaborator>>()
  }

  async updateCollaborator(id: string, data: Record<string, unknown>) {
    return this.client
      .patch(this.url(`collaborators/${id}`), { json: data })
      .json<ApiDataResponse<Collaborator>>()
  }

  async deleteCollaborator(id: string) {
    return this.client.delete(this.url(`collaborators/${id}`)).json<ApiDataResponse<null>>()
  }

  async activateCollaborator(id: string) {
    return this.client
      .patch(this.url(`collaborators/${id}/activate`))
      .json<ApiDataResponse<Collaborator>>()
  }

  async suspendCollaborator(id: string) {
    return this.client
      .patch(this.url(`collaborators/${id}/suspend`))
      .json<ApiDataResponse<Collaborator>>()
  }

  async terminateCollaborator(id: string) {
    return this.client
      .patch(this.url(`collaborators/${id}/terminate`))
      .json<ApiDataResponse<Collaborator>>()
  }

  // Arrivals API - Inventory arrivals with cost allocation
  async getArrivals(params?: {
    status?: string
    owner_type?: string
    collaborator_id?: string
    date_from?: string
    date_to?: string
    sort_by?: string
    sort_direction?: 'asc' | 'desc'
    page?: number
    per_page?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.owner_type) searchParams.set('owner_type', params.owner_type)
    if (params?.collaborator_id) searchParams.set('collaborator_id', params.collaborator_id)
    if (params?.date_from) searchParams.set('date_from', params.date_from)
    if (params?.date_to) searchParams.set('date_to', params.date_to)
    if (params?.sort_by) searchParams.set('sort_by', params.sort_by)
    if (params?.sort_direction) searchParams.set('sort_direction', params.sort_direction)
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())

    const endpoint = searchParams.toString() ? `arrivals?${searchParams}` : 'arrivals'
    return this.client.get(this.url(endpoint)).json<ApiCollectionResponse<ArrivalSummary>>()
  }

  async getArrival(id: string) {
    return this.client.get(this.url(`arrivals/${id}`)).json<ApiDataResponse<ArrivalDetailed>>()
  }

  async createArrival(data: Record<string, unknown>) {
    return this.client
      .post(this.url('arrivals'), { json: data })
      .json<ApiDataResponse<ArrivalDetailed>>()
  }

  async updateArrival(id: string, data: Record<string, unknown>) {
    return this.client
      .patch(this.url(`arrivals/${id}`), { json: data })
      .json<ApiDataResponse<ArrivalDetailed>>()
  }

  async deleteArrival(id: string) {
    return this.client.delete(this.url(`arrivals/${id}`)).json<ApiDataResponse<null>>()
  }

  // Arrival status transitions
  async receiveArrival(id: string, data?: { arrived_at?: string }) {
    return this.client
      .patch(this.url(`arrivals/${id}/receive`), { json: data || {} })
      .json<ApiDataResponse<ArrivalDetailed>>()
  }

  async verifyArrival(
    id: string,
    data?: { items?: Array<{ id: string; quantity_received: number }> },
  ) {
    return this.client
      .patch(this.url(`arrivals/${id}/verify`), { json: data || {} })
      .json<ApiDataResponse<ArrivalDetailed>>()
  }

  async closeArrival(id: string) {
    return this.client
      .patch(this.url(`arrivals/${id}/close`))
      .json<ApiDataResponse<ArrivalDetailed>>()
  }

  // Arrival related data
  async getArrivalItems(id: string) {
    return this.client
      .get(this.url(`arrivals/${id}/items`))
      .json<ApiDataResponse<{ items: ArrivalItem[] }>>()
  }

  async getArrivalFinancialSummary(id: string) {
    return this.client
      .get(this.url(`arrivals/${id}/financial_summary`))
      .json<ApiDataResponse<ArrivalFinancialSummaryResponse>>()
  }

  async getArrivalItemsSummary(id: string) {
    return this.client
      .get(this.url(`arrivals/${id}/items_summary`))
      .json<ApiDataResponse<ArrivalItemsSummaryResponse>>()
  }

  async getArrivalsSummary() {
    return this.client
      .get(this.url('arrivals/summary'))
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  // Add item to existing arrival (draft only)
  async addArrivalItem(arrivalId: string, data: any) {
    return this.client
      .post(this.url(`arrivals/${arrivalId}/items`), { json: data })
      .json<ApiDataResponse<Record<string, unknown>>>()
  }

  // ============================================
  // Payments API - Payment recording and tracking
  // ============================================

  // Get list of available payment methods (for checkout/forms)
  async getPaymentMethods() {
    return this.client
      .get(this.url('payment_methods/available'))
      .json<
        ApiDataResponse<{
          payment_methods: PaymentMethodSchema[]
          grouped_by_category?: Record<string, PaymentMethodSchema[]>
        }>
      >()
  }

  // Get list of payment methods (admin view)
  async getPaymentMethodsAdmin() {
    return this.client
      .get(this.url('payment_methods'))
      .json<ApiCollectionResponse<PaymentMethodSchema>>()
  }

  // Update a payment method (admin)
  async updatePaymentMethod(
    id: string,
    data: {
      payment_method: {
        display_name?: string
        active?: boolean
        position?: number
        minimum_amount?: number | null
        maximum_amount?: number | null
        provider_settings?: Record<string, unknown>
      }
    },
  ) {
    return this.client
      .patch(this.url(`payment_methods/${id}`), { json: data })
      .json<ApiDataResponse<PaymentMethodSchema>>()
  }

  // Create default payment methods (admin)
  async createDefaultPaymentMethods() {
    return this.client
      .post(this.url('payment_methods/create_defaults'))
      .json<ApiDataResponse<{ created: PaymentMethodSchema[]; message?: string }>>()
  }

  // Get list of payments with filters
  async getPayments(params?: {
    customer_id?: string
    order_id?: string
    status?: string
    payment_method_id?: string
    payment_method_category?: string
    from_date?: string
    to_date?: string
    search?: string
    page?: number
    per_page?: number
    sort_by?: string
    sort_direction?: string
  }) {
    const searchParams = new URLSearchParams()
    if (params?.customer_id) searchParams.set('customer_id', params.customer_id)
    if (params?.order_id) searchParams.set('order_id', params.order_id)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.payment_method_id) searchParams.set('payment_method_id', params.payment_method_id)
    if (params?.payment_method_category)
      searchParams.set('payment_method_category', params.payment_method_category)
    if (params?.from_date) searchParams.set('from_date', params.from_date)
    if (params?.to_date) searchParams.set('to_date', params.to_date)
    if (params?.search) searchParams.set('search', params.search)
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
    if (params?.sort_by) searchParams.set('sort_by', params.sort_by)
    if (params?.sort_direction) searchParams.set('sort_direction', params.sort_direction)

    const endpoint = searchParams.toString() ? `payments?${searchParams}` : 'payments'
    return this.client.get(this.url(endpoint)).json<ApiCollectionResponse<Payment>>()
  }

  // Get payments awaiting confirmation
  async getPendingPayments(params?: { per_page?: number }) {
    const searchParams = new URLSearchParams()
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())

    const endpoint = searchParams.toString()
      ? `payments/pending_confirmation?${searchParams}`
      : 'payments/pending_confirmation'
    return this.client.get(this.url(endpoint)).json<ApiCollectionResponse<PaymentDetailed>>()
  }

  // Get single payment details
  async getPayment(id: string) {
    return this.client.get(this.url(`payments/${id}`)).json<ApiDataResponse<PaymentDetailed>>()
  }

  // Create a new payment
  async createPayment(data: {
    payment: {
      customer_id: string
      order_id?: string
      amount: number
      payment_method_id: string
      payment_date?: string
      reference_number?: string
      notes?: string
      provider_metadata?: {
        phone?: string
        account_holder?: string
      }
    }
  }) {
    return this.client
      .post(this.url('payments'), { json: data })
      .json<ApiDataResponse<PaymentDetailed>>()
  }

  // Update a payment (pending only)
  async updatePayment(
    id: string,
    data: {
      payment: {
        amount?: number
        payment_date?: string
        reference_number?: string
        notes?: string
      }
    },
  ) {
    return this.client
      .patch(this.url(`payments/${id}`), { json: data })
      .json<ApiDataResponse<PaymentDetailed>>()
  }

  // Cancel a payment
  async cancelPayment(id: string, reason?: string) {
    const json = reason ? { reason } : undefined
    return this.client
      .delete(this.url(`payments/${id}`), { json })
      .json<ApiDataResponse<PaymentDetailed>>()
  }

  // Confirm a payment (immediate confirmation)
  async confirmPayment(id: string, data?: { confirmed_at?: string }) {
    return this.client
      .patch(this.url(`payments/${id}/confirm`), { json: data || {} })
      .json<ApiDataResponse<PaymentDetailed>>()
  }

  // Manual confirmation (admin override)
  async manualConfirmPayment(id: string, data?: { reason?: string; confirmed_at?: string }) {
    return this.client
      .post(this.url(`payments/${id}/manual_confirm`), { json: data || {} })
      .json<ApiDataResponse<PaymentDetailed>>()
  }

  // Allocate payment to an order
  async allocatePayment(paymentId: string, orderId: string) {
    return this.client
      .post(this.url(`payments/${paymentId}/allocate`), { json: { order_id: orderId } })
      .json<ApiDataResponse<PaymentDetailed>>()
  }

  // Deallocate payment from order
  async deallocatePayment(paymentId: string) {
    return this.client
      .delete(this.url(`payments/${paymentId}/allocate`))
      .json<ApiDataResponse<PaymentDetailed>>()
  }

  // Check provider status for a payment
  async getPaymentProviderStatus(id: string) {
    return this.client
      .get(this.url(`payments/${id}/provider_status`))
      .json<ApiDataResponse<PaymentDetailed>>()
  }

  // Get customer payment history
  async getCustomerPayments(customerId: string, params?: { page?: number; per_page?: number }) {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())

    const endpoint = searchParams.toString()
      ? `customers/${customerId}/payments?${searchParams}`
      : `customers/${customerId}/payments`
    return this.client.get(this.url(endpoint)).json<ApiCollectionResponse<Payment>>()
  }

  // Get customer financial summary
  async getCustomerFinancialSummary(customerId: string) {
    return this.client
      .get(this.url(`customers/${customerId}/financial_summary`))
      .json<ApiDataResponse<CustomerFinancialSummary>>()
  }

  // Auto-allocate customer payments to orders
  async autoAllocateCustomerPayments(customerId: string) {
    return this.client
      .post(this.url(`customers/${customerId}/auto_allocate_payments`))
      .json<ApiDataResponse<AutoAllocateResponse>>()
  }

  // Get payments for a specific order
  async getOrderPayments(orderId: string) {
    return this.client
      .get(this.url(`orders/${orderId}/payments`))
      .json<ApiDataResponse<PaymentSummarySchema[]>>()
  }

  // ============================================
  // Settlements API - Collaborator Settlements
  // ============================================

  // Get list of settlements with filters
  async getSettlements(params?: {
    collaborator_id?: string
    status?: string
    page?: number
    per_page?: number
    sort_by?: string
    sort_direction?: string
  }) {
    const searchParams = new URLSearchParams()
    if (params?.collaborator_id) searchParams.set('collaborator_id', params.collaborator_id)
    if (params?.status) searchParams.set('status', params.status)
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.per_page) searchParams.set('per_page', params.per_page.toString())
    if (params?.sort_by) searchParams.set('sort_by', params.sort_by)
    if (params?.sort_direction) searchParams.set('sort_direction', params.sort_direction)

    const endpoint = searchParams.toString()
      ? `collaborator_settlements?${searchParams}`
      : 'collaborator_settlements'
    return this.client.get(this.url(endpoint)).json<ApiCollectionResponse<Settlement>>()
  }

  // Get single settlement details
  async getSettlement(id: string) {
    return this.client
      .get(this.url(`collaborator_settlements/${id}`))
      .json<ApiDataResponse<Settlement>>()
  }

  // Create a new settlement
  async createSettlement(data: {
    collaborator_id: string
    period_start: string
    period_end: string
  }) {
    return this.client
      .post(this.url('collaborator_settlements'), { json: data })
      .json<ApiDataResponse<Settlement>>()
  }

  // Calculate settlement amounts
  async calculateSettlement(id: string) {
    return this.client
      .post(this.url(`collaborator_settlements/${id}/calculate`))
      .json<ApiDataResponse<Settlement>>()
  }

  // Approve a settlement
  async approveSettlement(id: string) {
    return this.client
      .patch(this.url(`collaborator_settlements/${id}/approve`))
      .json<ApiDataResponse<Settlement>>()
  }

  // Mark settlement as paid
  async markSettlementAsPaid(id: string, paymentDate?: string) {
    const json = paymentDate ? { payment_date: paymentDate } : undefined
    return this.client
      .patch(this.url(`collaborator_settlements/${id}/mark_as_paid`), { json })
      .json<ApiDataResponse<Settlement>>()
  }

  // Dispute a settlement
  async disputeSettlement(id: string, reason: string) {
    return this.client
      .patch(this.url(`collaborator_settlements/${id}/dispute`), { json: { reason } })
      .json<ApiDataResponse<Settlement>>()
  }

  // Get unsettled summary for a collaborator
  async getUnsettledSummary(collaboratorId: string) {
    return this.client
      .get(this.url(`collaborator_settlements/unsettled_summary?collaborator_id=${collaboratorId}`))
      .json<ApiDataResponse<UnsettledSummary>>()
  }

  // ============================================
  // Analytics API - Dashboard & Reporting
  // ============================================

  async getDashboardSummary(params?: { period?: string; compare_to_previous?: boolean }) {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.compare_to_previous) searchParams.set('compare_to_previous', 'true')

    const endpoint = searchParams.toString()
      ? `analytics/dashboard_summary?${searchParams}`
      : 'analytics/dashboard_summary'
    return this.client.get(this.url(endpoint)).json<ApiDataResponse<DashboardSummaryResponse>>()
  }

  async getSalesAnalytics(params?: {
    period?: string
    granularity?: string
    breakdown_by?: string
    compare_to_previous?: boolean
  }) {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.granularity) searchParams.set('granularity', params.granularity)
    if (params?.breakdown_by) searchParams.set('breakdown_by', params.breakdown_by)
    if (params?.compare_to_previous) searchParams.set('compare_to_previous', 'true')

    const endpoint = searchParams.toString()
      ? `analytics/sales_analytics?${searchParams}`
      : 'analytics/sales_analytics'
    return this.client.get(this.url(endpoint)).json<ApiDataResponse<SalesAnalyticsResponse>>()
  }

  async getCustomerAnalytics(params?: {
    period?: string
    segment?: string
    sort_by?: string
    limit?: number
  }) {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.segment) searchParams.set('segment', params.segment)
    if (params?.sort_by) searchParams.set('sort_by', params.sort_by)
    if (params?.limit) searchParams.set('limit', params.limit.toString())

    const endpoint = searchParams.toString()
      ? `analytics/customer_analytics?${searchParams}`
      : 'analytics/customer_analytics'
    return this.client.get(this.url(endpoint)).json<ApiDataResponse<CustomerAnalyticsResponse>>()
  }

  async getDeliveryAnalytics(params?: {
    period?: string
    deliverer_id?: string
    delivery_type?: string
    zone_id?: string
    collection_method?: string
    start_date?: string
    end_date?: string
  }) {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.deliverer_id) searchParams.set('deliverer_id', params.deliverer_id)
    if (params?.delivery_type) searchParams.set('delivery_type', params.delivery_type)
    if (params?.zone_id) searchParams.set('zone_id', params.zone_id)
    if (params?.collection_method) searchParams.set('collection_method', params.collection_method)
    if (params?.start_date) searchParams.set('start_date', params.start_date)
    if (params?.end_date) searchParams.set('end_date', params.end_date)

    const endpoint = searchParams.toString()
      ? `analytics/delivery_analytics?${searchParams}`
      : 'analytics/delivery_analytics'
    return this.client.get(this.url(endpoint)).json<ApiDataResponse<DeliveryAnalyticsResponse>>()
  }

  async getFinancialOverview(params?: { period?: string; compare_to_previous?: boolean }) {
    const searchParams = new URLSearchParams()
    if (params?.period) searchParams.set('period', params.period)
    if (params?.compare_to_previous) searchParams.set('compare_to_previous', 'true')

    const endpoint = searchParams.toString()
      ? `analytics/financial_overview?${searchParams}`
      : 'analytics/financial_overview'
    return this.client.get(this.url(endpoint)).json<ApiDataResponse<FinancialOverviewResponse>>()
  }

  // ===== Settings API =====

  // Get delivery fee settings (admin only)
  async getDeliveryFeeSettings() {
    return this.client.get(this.url('settings/delivery_fees')).json<{
      success: boolean
      delivery_fees: {
        local: {
          method: string
          fixed_fee: number | null
          percentage: number | null
          distance_rate_per_km: number | null
          free_delivery_threshold: number | null
          minimum_fee: number | null
          maximum_fee: number | null
          available_methods: string[]
        }
        cross_city: {
          method: string
          fixed_fee: number | null
          minimum_fee: number | null
          maximum_fee: number | null
          available_methods: string[]
        }
      }
    }>()
  }

  // Update delivery fee settings (admin only)
  async updateDeliveryFeeSettings(data: {
    local?: {
      method?: string
      fixed_fee?: number
      percentage?: number
      distance_rate_per_km?: number
      free_delivery_threshold?: number
      minimum_fee?: number
      maximum_fee?: number
    }
    cross_city?: {
      method?: string
      fixed_fee?: number
      minimum_fee?: number
      maximum_fee?: number
    }
  }) {
    return this.client.put(this.url('settings/delivery_fees'), { json: data }).json<{
      success: boolean
      message: string
      delivery_fees: {
        local: {
          method: string
          fixed_fee: number | null
          percentage: number | null
          distance_rate_per_km: number | null
          free_delivery_threshold: number | null
          minimum_fee: number | null
          maximum_fee: number | null
          available_methods: string[]
        }
        cross_city: {
          method: string
          fixed_fee: number | null
          minimum_fee: number | null
          maximum_fee: number | null
          available_methods: string[]
        }
      }
    }>()
  }

  // Get provider cost settings (admin only)
  async getProviderCostSettings() {
    return this.client.get(this.url('settings/provider_costs')).json<{
      success: boolean
      provider_costs: {
        method: string
        default_percentage: number
        fixed_cost: number | null
        shipper_rates: Record<string, number>
        available_methods: string[]
        available_shippers: Array<{ id: string; name: string }>
      }
    }>()
  }

  // Update provider cost settings (admin only)
  async updateProviderCostSettings(data: {
    method?: string
    default_percentage?: number
    fixed_cost?: number
    shipper_rates?: Record<string, string | number>
  }) {
    return this.client.put(this.url('settings/provider_costs'), { json: data }).json<{
      success: boolean
      message: string
      provider_costs: {
        method: string
        default_percentage: number
        fixed_cost: number | null
        shipper_rates: Record<string, number>
        available_methods: string[]
        available_shippers: Array<{ id: string; name: string }>
      }
    }>()
  }

  // Get delivery fee estimate (for order creation)
  async getDeliveryFeeEstimate(params: {
    zone_id?: string
    shipper_office_zone_id?: string
    order_total?: number
    distance_km?: number
    is_cross_city?: boolean
  }) {
    const searchParams = new URLSearchParams()
    if (params.zone_id) searchParams.set('zone_id', params.zone_id)
    if (params.shipper_office_zone_id)
      searchParams.set('shipper_office_zone_id', params.shipper_office_zone_id)
    if (params.order_total !== undefined)
      searchParams.set('order_total', params.order_total.toString())
    if (params.distance_km !== undefined)
      searchParams.set('distance_km', params.distance_km.toString())
    if (params.is_cross_city !== undefined)
      searchParams.set('is_cross_city', params.is_cross_city.toString())

    const endpoint = searchParams.toString()
      ? `deliveries/fee_estimate?${searchParams}`
      : 'deliveries/fee_estimate'
    return this.client.get(this.url(endpoint)).json<{
      success: boolean
      fee_estimate: {
        fee: number
        method: string
        details: Record<string, unknown>
        waived: boolean
        waiver_reason: string | null
      }
      organization_policy: {
        method: string
        free_delivery_threshold?: number
        minimum_fee?: number
        maximum_fee?: number
      }
    }>()
  }

  // Generic methods for future endpoints (with better typing)
  async get<T>(endpoint: string): Promise<T> {
    return this.client.get(this.url(endpoint)).json<T>()
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.client.post(this.url(endpoint), { json: data }).json<T>()
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.client.put(this.url(endpoint), { json: data }).json<T>()
  }

  // Branch Access Management
  async getBranchAccesses(filters?: {
    user_id?: string
    org_branch_id?: string
    active?: boolean
  }) {
    const searchParams = new URLSearchParams()
    if (filters?.user_id) searchParams.set('user_id', filters.user_id)
    if (filters?.org_branch_id) searchParams.set('org_branch_id', filters.org_branch_id)
    if (filters?.active !== undefined) searchParams.set('active', String(filters.active))

    const query = searchParams.toString()
    // The endpoint wraps the collection in `{ data: [...] }`; unwrap so the
    // declared BranchAccess[] return type matches the runtime value (otherwise
    // DataTable receives an object and iterates its keys).
    const response = await this.client
      .get(this.url(`branch_accesses${query ? `?${query}` : ''}`))
      .json<{ data: BranchAccess[] } | BranchAccess[]>()
    return Array.isArray(response) ? response : response.data
  }

  async createBranchAccess(data: {
    user_id: string
    org_branch_id: string
    expires_at?: string | null
    notes?: string
  }) {
    return this.client
      .post(this.url('branch_accesses'), {
        json: { branch_access: data },
      })
      .json<BranchAccess>()
  }

  async deleteBranchAccess(id: string) {
    return this.client.delete(this.url(`branch_accesses/${id}`)).json()
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.client.delete(this.url(endpoint)).json<T>()
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.client.patch(this.url(endpoint), { json: data }).json<T>()
  }

  // Alias for backwards compatibility
  async feeEstimate(params: Parameters<typeof this.getDeliveryFeeEstimate>[0]) {
    return this.getDeliveryFeeEstimate(params)
  }

  // ===========================================================================
  // Live Sessions (feature-gated: organizations.features.live_shopping)
  // ===========================================================================
  async getLiveSessions(params?: { status?: string; page?: number }) {
    const sp = new URLSearchParams()
    if (params?.status) sp.set('status', params.status)
    if (params?.page) sp.set('page', String(params.page))
    const endpoint = sp.toString() ? `live_sessions?${sp}` : 'live_sessions'
    return this.client.get(this.url(endpoint)).json<{ data: any[]; meta: any }>()
  }

  async getLiveSession(id: string) {
    return this.client.get(this.url(`live_sessions/${id}`)).json<{ data: any }>()
  }

  async getLiveSessionSummary(id: string) {
    return this.client.get(this.url(`live_sessions/${id}/summary`)).json<{ data: any }>()
  }

  async createLiveSession(data: {
    title: string
    platform?: string
    external_session_url?: string
    notes?: string
  }) {
    return this.client
      .post(this.url('live_sessions'), { json: { live_session: data } })
      .json<{ data: any }>()
  }

  async endLiveSession(id: string) {
    return this.client.post(this.url(`live_sessions/${id}/end`)).json<{ data: any }>()
  }

  async reconcileLiveSession(id: string, body: { confirm?: string[]; cancel?: string[] }) {
    return this.client
      .post(this.url(`live_sessions/${id}/reconcile`), { json: body })
      .json<{
        data: any
        meta: {
          confirmed: number
          cancelled: number
          promoted: Array<{
            sale_id: string
            product_sku?: string
            runner_up: { id: string; customer: string; customer_id: string; priority: string; priority_name: string }
          }>
        }
      }>()
  }

  async addLiveSessionRunnerUp(id: string, body: { product_item_id: string; customer_id: string }) {
    return this.client
      .post(this.url(`live_sessions/${id}/runner_up`), { json: body })
      .json<{ data: { runner_up: { customer: string }; holder: string; product_sku?: string } }>()
  }

  // ===========================================================================
  // Organization features (admin-only flag flips)
  // ===========================================================================
  async updateFeatures(features: Record<string, boolean>) {
    return this.client
      .patch(this.url('organizations/current/features'), { json: { features } })
      .json<{ data: { features: Record<string, unknown> } }>()
  }
}

export const apiClient = new ApiClient()

// =============================================================================
// Paperstore Direct Client
// =============================================================================
// Direct client for hud-paperstore Go service (PDF generation)
// Uses JWT auth, same as Rails API
//
// Contract enforcement:
// - Types generated from proto → OpenAPI → TypeScript
// - Proto files are single source of truth
// - Any API change requires: proto update → buf generate → pnpm generate-paperstore-api
// - See: hud-paperstore/docs/adr/001-grpc-over-rest.md

import type { components as PaperstoreComponents } from '@/types/paperstore-api'

// Re-export generated types for use in composables and components
export type PaperSizeInfo = PaperstoreComponents['schemas']['PaperSizeInfo']
export type DeliveryEntry = PaperstoreComponents['schemas']['DeliveryEntry']
export type Money = PaperstoreComponents['schemas']['Money']
export type PdfConfig = PaperstoreComponents['schemas']['PdfConfig']
export type GenerateDeliveryPdfRequest =
  PaperstoreComponents['schemas']['GenerateDeliveryPdfRequest']
export type GenerateDeliveryPdfResponse =
  PaperstoreComponents['schemas']['GenerateDeliveryPdfResponse']
export type GetSupportedPaperSizesResponse =
  PaperstoreComponents['schemas']['GetSupportedPaperSizesResponse']

const PAPERSTORE_BASE_URL = import.meta.env.VITE_PAPERSTORE_URL || 'http://localhost:8080'

class PaperstoreClient {
  public readonly client: typeof ky
  private readonly baseUrl: string

  constructor() {
    this.baseUrl = PAPERSTORE_BASE_URL
    this.client = ky.create({
      headers: {
        'Content-Type': 'application/json',
      },
      retry: {
        // Same policy as ApiClient: retry transient + rate-limit, honor Retry-After.
        limit: 3,
        statusCodes: [408, 429, 500, 502, 503, 504],
        backoffLimit: 30000,
      },
      hooks: {
        beforeRequest: [
          async (request) => {
            const authStore = useAuthStore()
            const token = authStore.token
            if (token) {
              request.headers.set('Authorization', `Bearer ${token}`)
            }
          },
        ],
      },
    })
  }

  // Helper to build full URL
  public url(path: string): string {
    const cleanPath = path.startsWith('/') ? path.slice(1) : path
    const cleanBase = this.baseUrl.endsWith('/') ? this.baseUrl.slice(0, -1) : this.baseUrl
    return `${cleanBase}/${cleanPath}`
  }

  /**
   * Health check (not contract-enforced, simple endpoint)
   */
  async health(): Promise<{ status: string; service: string; timestamp: string }> {
    return this.client.get(this.url('health')).json()
  }

  /**
   * Get supported paper sizes
   * Contract: GET /v1/pdf/paper-sizes → GetSupportedPaperSizesResponse
   */
  async getPaperSizes(): Promise<GetSupportedPaperSizesResponse> {
    return this.client.get(this.url('v1/pdf/paper-sizes')).json()
  }

  /**
   * Generate delivery PDF from provided data
   * Contract: POST /v1/pdf/deliveries → Binary PDF (via pdfProxyHandler)
   *
   * Note: grpc-gateway returns JSON with base64 pdf_content,
   * but our pdfProxyHandler extracts raw binary for browser download.
   */
  async generateDeliveryPdf(params: GenerateDeliveryPdfRequest): Promise<Blob> {
    const response = await this.client.post(this.url('v1/pdf/deliveries'), {
      json: params,
    })
    return response.blob()
  }
}

export const paperstoreClient = new PaperstoreClient()

export default apiClient
